import {
    AreaCode,
    areaCodeSchema,
} from '@west-australian-newspapers/election-api-types'

/** The feature toggle prefixes. E.g. 'federal-', 'wa-'. */
type ElectionPrefix = `${Lowercase<AreaCode>}-`

/**
 * Features scoped to a single election, kept prefixed per election.
 *
 * These gate that election's own routes, links and page widgets, all of which
 * outlive the campaign -- federal-election-2025's results pages are still
 * served long after the VIC election starts, and each needs turning on and
 * off on its own.
 */
const perElectionFeatures = [
    // Routes and links scoped to this election's own URLs.
    `election-landing-page-route`,
    `election-navigation-live-blog`,
    `election-electorate-results-route`,
    `election-party-totals-route`,
    `election-map-route`,
    `election-pollie-rater`,
    `election-sitewide-navigation`,
    // Widgets on this election's own pages.
    `election-landing-page-the-seats`,
    `election-landing-page-the-race`,
    `election-landing-page-the-swing`,
    `election-landing-page-map`,
    `election-section-head-to-head`,
    `election-head-to-head-countdown`,
    `election-navigation`,
    `election-live-title-prefix`,
    `election-party-totals-tpp-estimate-widget`,
    // The politics section curation, chosen by its own wan-election-id.
    `election-politics-collection`,
    `election-politics-collection-position`,
] as const

/**
 * Homepage widget toggles, shared across elections.
 *
 * The homepage shows one election at a time, chosen by
 * `homepage-election-wan-election-id`, so a prefix per election buys nothing
 * here. These resolve to a single shared toggle (e.g.
 * `election-homepage-the-race`) and a new election inherits them rather than
 * needing its own set created first.
 *
 * Everything outside the homepage stays per-election -- an election's own
 * landing, electorate, party-totals and map pages outlive the campaign and
 * need configuring independently of whatever is on the homepage today.
 */
const sharedElectionFeatures = [
    `election-homepage-the-seats`,
    `election-homepage-the-race`,
    `election-homepage-collection`,
    `election-homepage-collection-position`,
    `election-homepage-head-to-head`,
    `election-homepage-head-to-head-nav-top`,
    `election-homepage-the-swing`,
    `election-homepage-map`,
    `election-homepage-pollie-rater-promo`,
] as const

const specificElectionFeatures = [
    ...perElectionFeatures,
    ...sharedElectionFeatures,
] as const

/** Features that need to be controlled individually to each state/federal election. */
type SpecificElectionFeatures = (typeof specificElectionFeatures)[number]

/** Features resolved from a single shared toggle, not one per election. */
type SharedElectionFeatures = (typeof sharedElectionFeatures)[number]

const isSharedElectionFeature = (
    feature: SpecificElectionFeatures,
): feature is SharedElectionFeatures =>
    (sharedElectionFeatures as readonly string[]).includes(feature)

export type ElectionFeatures =
    | 'pollie-rater-drivers'
    | 'homepage-election-component-id'
    | 'federal-election-2022-pollie-rater-strap-driver'
    | 'federal-election-2022-live-blog-drivers'
    // | 'federal-election-head-to-head-countdown'
    | 'election-widget-test-page'
    | 'election-widget-preview-page'
    | 'custom-election-embeds'
    /** These values below determine the elections to show on non-election pages */
    | 'article-header-wan-election-id'
    | 'homepage-election-wan-election-id'
    | 'politics-curated-section-wan-election-id'
    | 'election-data-disclaimer'

    /** The shared homepage toggles, one per feature rather than one per election. */
    | SharedElectionFeatures

    /** All combinations of prefixes and specific-election features. */
    | `${ElectionPrefix}${SpecificElectionFeatures}`

/**
 * Resolves an election id to its toggle prefix, e.g. `federal-election-2025`
 * to `federal` and `vic-election-2026` to `vic`. Falls back to the whole id
 * when it does not name a known area code.
 */
const getElectionPrefix = (electionId: string): string => {
    if (electionId.startsWith('federal')) {
        return 'federal'
    }

    const result = areaCodeSchema.safeParse(
        electionId.split('-')[0].toUpperCase(),
    )

    return result.success ? result.data.toLowerCase() : electionId
}

/**
 * Returns the toggle name governing each feature for a specific election.
 *
 * Per-election features get the election's prefix; shared homepage features
 * resolve to their bare name, since one set covers every election.
 */
export const getElectionToggles = (
    electionId: string,
): Record<SpecificElectionFeatures, string> => {
    const prefix = getElectionPrefix(electionId)

    return Object.fromEntries(
        specificElectionFeatures.map((feature) => [
            feature,
            isSharedElectionFeature(feature) ? feature : `${prefix}-${feature}`,
        ]),
    ) as Record<SpecificElectionFeatures, string>
}
